import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReEvent, Id, ReSession } from "../../interfaces/types";
import { useUserContext } from "../../providers/userProvider";
import { useEventContext } from "../../providers/EventProvider";
import { useSessionContext } from "../../providers/SessionProvider";
import { usePermissionContext } from "../../providers/PermissionProvider";
import { useCustomThemeContext } from "../../providers/customThemeProvider";
import { Box, Button, Container, Typography } from "@mui/material";
import Grid from '@mui/material/Grid2';
import ReviewColumn from "../../components/reviews/ReviewColumn";
import dayjs from 'dayjs';


const SessionViewPage: FC = () => {
    const navigate = useNavigate();
    const { theme, updateTheme } = useCustomThemeContext();
    const { sessionId } = useParams<string>();
    const { getUserById } = useUserContext();
    const { getSessionById } = useSessionContext();
    const { getEventBySessionId, isXpFactorActive } = useEventContext();
    const { hasSessionOverviewEditPermission, hasReviewAndCommentViewPermission } = usePermissionContext();

    const [session, setSession] = useState<ReSession>();
    const [currentEvent, setCurrentEvent] = useState<ReEvent>();

    //size setting of grid
    const spacing = 2;
    const mainLeftWidth = 8;
    const mainRightWidth = 4;
    const subColumnWidth = 12;
    const subRowWidth = 12;
    const subRowWidthLeft = 4;
    const subRowWidthRight = 8;

    useEffect(() => {
        const fetchData = async () => {
            if (sessionId) {
                const sessionData = await getSessionById(sessionId as Id);
                setSession(sessionData);
                const foundEvent = await getEventBySessionId(sessionId as Id);
                if (foundEvent) {
                    setCurrentEvent(foundEvent as ReEvent);
                    updateTheme(foundEvent);
                }
            }
        };

        fetchData();
    }, [sessionId, getSessionById]);


    return (
        <Container disableGutters>
            <Box sx={{ flexGrow: 1, width: '100%', }}>
                <Grid container
                    direction="row"
                    sx={{
                        justifyContent: "center",
                        alignItems: "flex-start",
                        paddingTop: 2, paddingBottom: 2,
                    }}
                    spacing={spacing}>
                    <Grid size={mainLeftWidth}>
                        <Grid container
                            direction="column"
                            sx={{
                                justifyContent: "center",
                                alignItems: "flex-start",
                            }}
                            spacing={spacing}>
                            {/**title */}
                            {session?.title && <Grid size={subRowWidth}>
                                <Typography variant='h3'
                                    sx={{
                                        color: theme.palette.primary.main,
                                    }}>
                                    {session.title}
                                </Typography>
                            </Grid>}
                            {/**subtitle */}
                            {session?.subtitle && <Grid size={subRowWidth}>
                                <Typography variant='h5'
                                    sx={{
                                        borderBottom: 5, borderBottomColor: 'primary.main',
                                    }}>
                                    {session.subtitle}
                                </Typography>
                            </Grid>}
                            {/**short description */}
                            {session?.shortDescription && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="column"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subColumnWidth}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            Short description:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subColumnWidth}>
                                        <Typography variant='body1'>
                                            {session.shortDescription}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {/**long description */}
                            {session?.longDescription && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="column"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subColumnWidth}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            Long description:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subColumnWidth}>
                                        <Typography variant='body1'>
                                            {session.longDescription}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {/**outline/timetable */}
                            {session?.outlineOrTimetable && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="column"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subColumnWidth}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            Outline / Timetable:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subColumnWidth}>
                                        <Typography variant='body1' sx={{
                                            pb: 2,
                                            borderBottom: 5, borderBottomColor: 'primary.main',
                                        }}>
                                            {session.outlineOrTimetable}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {/**presented by */}
                            {session?.presenterIds && session.presenterIds.length > 0 && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="row"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subRowWidthLeft}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            Presented by:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subRowWidthRight}>
                                        <Typography variant='body1'>
                                            {session.presenterIds.map(presenter => `${getUserById(presenter)?.firstName} ${getUserById(presenter)?.lastName}`).join(', ')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {/**topic */}
                            {session?.topic && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="row"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subRowWidthLeft}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            Topic:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subRowWidthRight}>
                                        <Typography variant='body1'>
                                            {currentEvent?.eventConfiguration?.topics?.find(topic => topic.id === session?.topic)?.name}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {/**intended audience */}
                            {session?.intendedAudience && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="row"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subRowWidthLeft}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            Intended audience:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subRowWidthRight}>
                                        <Typography variant='body1'>
                                            {session.intendedAudience}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {/**experience */}
                            {session?.experienceLevel && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="row"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subRowWidthLeft}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            Experience:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subRowWidthRight}>
                                        <Typography variant='body1'>
                                            {session?.experienceLevel}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {/**duration */}
                            {session?.duration && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="row"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subRowWidthLeft}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            Duration:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subRowWidthRight}>
                                        <Typography variant='body1'>
                                            {currentEvent?.eventConfiguration?.possibleDurations?.find(option => option.id === session?.duration)?.duration} minutes
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {/**Room setup */}
                            {session?.roomSetup && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="row"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subRowWidthLeft}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            Room setup:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subRowWidthRight}>
                                        <Typography variant='body1'>
                                            {currentEvent?.eventConfiguration?.roomSetups?.find(roomSetup => roomSetup.id === session?.roomSetup)?.name}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {/**Max Participants */}
                            {session?.maxParticipants && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="row"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subRowWidthLeft}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            Max Participants:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subRowWidthRight}>
                                        <Typography variant='body1'>
                                            {session.maxParticipants}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {/**XP-factor */}
                            {currentEvent && isXpFactorActive(currentEvent) && session?.xpFactor && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="row"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subRowWidthLeft}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            XP-Factor:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subRowWidthRight}>
                                        <Typography variant='body1' sx={{ color: 'black' }}>
                                            {session.xpFactor}<span style={{ color: theme.palette.primary.main }}>/10</span>
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {/**Laptops required */}
                            {session?.laptopsRequired && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="row"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subRowWidthLeft}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            Laptops required:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subRowWidthRight}>
                                        <Typography variant='body1'>
                                            {session.laptopsRequired ? 'Yes' : 'No'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {/**goal */}
                            {session?.sessionGoal && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="row"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subRowWidthLeft}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            Goal:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subRowWidthRight}>
                                        <Typography variant='body1'>
                                            {session.sessionGoal}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {/**Materials needed */}
                            {session?.materialsNeeded && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="row"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subRowWidthLeft}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            Materials needed:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subRowWidthRight}>
                                        <Typography variant='body1' sx={{ color: 'black' }}>
                                            {session.materialsNeeded}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {/**Materials url (+ material url description) */}
                            {session?.materialUrl && <Grid size={subRowWidth}>
                                <Grid container
                                    direction="row"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                    }}
                                    spacing={spacing}>
                                    <Grid size={subRowWidthLeft}>
                                        <Typography variant='h6'
                                            sx={{
                                                color: theme.palette.primary.main,
                                            }}>
                                            Link to materials:
                                        </Typography>
                                    </Grid>
                                    <Grid size={subRowWidthRight}>
                                        <Typography variant='body1'>
                                            <a
                                                href={session.materialUrl.startsWith('http') ? session.materialUrl : `http://${session.materialUrl}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ textDecoration: 'none', color: theme.palette.links.main }}
                                                onMouseDown={(e) => e.currentTarget.style.color = theme.palette.links.main}
                                                onMouseUp={(e) => e.currentTarget.style.color = theme.palette.clickedLinks.main}
                                            >
                                                {session?.materialDescription ? session.materialDescription : 'go to materials'}
                                            </a>
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>}
                            {currentEvent && hasSessionOverviewEditPermission(currentEvent.id) && <>
                                {/**manager+ only */}
                                <Grid size={subRowWidth}>
                                    <Typography variant='h5'
                                        sx={{
                                            borderTop: 5, borderTopColor: 'primary.main',
                                            color: theme.palette.primary.main,
                                        }}>
                                        MANAGER ONLY
                                    </Typography>
                                </Grid>
                                {/**Status */}
                                {session?.status && <Grid size={subRowWidth}>
                                    <Grid container
                                        direction="row"
                                        sx={{
                                            justifyContent: "center",
                                            alignItems: "flex-start",
                                        }}
                                        spacing={spacing}>
                                        <Grid size={subRowWidthLeft}>
                                            <Typography variant='h6'
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                }}>
                                                Status:
                                            </Typography>
                                        </Grid>
                                        <Grid size={subRowWidthRight}>
                                            <Typography variant='body1' sx={{ color: 'black' }}>
                                                {session.status}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>}
                                {/**Created by */}
                                {session?.createdBy && <Grid size={subRowWidth}>
                                    <Grid container
                                        direction="row"
                                        sx={{
                                            justifyContent: "center",
                                            alignItems: "flex-start",
                                        }}
                                        spacing={spacing}>
                                        <Grid size={subRowWidthLeft}>
                                            <Typography variant='h6'
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                }}>
                                                Created by:
                                            </Typography>
                                        </Grid>
                                        <Grid size={subRowWidthRight}>
                                            <Typography variant='body1' sx={{ color: 'black' }}>
                                                {getUserById(session.createdBy)?.firstName} {getUserById(session.createdBy)?.lastName}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>}
                                {/**Created at */}
                                {session?.createdAt && <Grid size={subRowWidth}>
                                    <Grid container
                                        direction="row"
                                        sx={{
                                            justifyContent: "center",
                                            alignItems: "flex-start",
                                        }}
                                        spacing={spacing}>
                                        <Grid size={subRowWidthLeft}>
                                            <Typography variant='h6'
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                }}>
                                                Created at:
                                            </Typography>
                                        </Grid>
                                        <Grid size={subRowWidthRight}>
                                            <Typography variant='body1' sx={{ color: 'black' }}>
                                                {dayjs(session.createdAt).format('DD/MM/YYYY - HH:mm')}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>}
                                {/**Last updated by */}
                                {session?.updatedBy && <Grid size={subRowWidth}>
                                    <Grid container
                                        direction="row"
                                        sx={{
                                            justifyContent: "center",
                                            alignItems: "flex-start",
                                        }}
                                        spacing={spacing}>
                                        <Grid size={subRowWidthLeft}>
                                            <Typography variant='h6'
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                }}>
                                                Last updated by:
                                            </Typography>
                                        </Grid>
                                        <Grid size={subRowWidthRight}>
                                            <Typography variant='body1' sx={{ color: 'black' }}>
                                                {getUserById(session.updatedBy)?.firstName} {getUserById(session.updatedBy)?.lastName}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>}
                                {/**Last updated at */}
                                {session?.updatedAt && <Grid size={subRowWidth}>
                                    <Grid container
                                        direction="row"
                                        sx={{
                                            justifyContent: "center",
                                            alignItems: "flex-start",
                                        }}
                                        spacing={spacing}>
                                        <Grid size={subRowWidthLeft}>
                                            <Typography variant='h6'
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                }}>
                                                Last updated at:
                                            </Typography>
                                        </Grid>
                                        <Grid size={subRowWidthRight}>
                                            <Typography variant='body1' sx={{ color: 'black' }}>
                                                {dayjs(session.updatedAt).format('DD/MM/YYYY - HH:mm')}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>}
                            </>}
                        </Grid>
                    </Grid>
                    {currentEvent?.eventConfiguration.areReviewsAndCommentsVisible && <Grid size={mainRightWidth}>
                        <Grid container
                            direction="column"
                            sx={{
                                justifyContent: "center",
                                alignItems: "flex-start",
                                borderLeft: 4, borderLeftColor: theme.palette.primary.main,
                                paddingLeft: 2,
                            }}
                            spacing={spacing}>
                            {currentEvent && <Grid size={subRowWidth}>
                                <Button
                                onClick={() => navigate(`/sessionlist/${currentEvent?.id}`)}
                                variant="contained"
                                sx={{width: '100%'}}
                                >
                                    Return to session list
                                </Button>
                            </Grid>}
                            {currentEvent && hasReviewAndCommentViewPermission(currentEvent?.id) && session &&
                                <Grid size={subRowWidth}>
                                    <ReviewColumn session={session} />
                                </Grid>
                            }
                        </Grid>
                    </Grid>}
                </Grid>
            </Box>
        </Container>
    );
};

export default SessionViewPage;